import { Checkbox, Col, Form, Icon, Input, message, Row, Select } from 'antd'
import DocumentsApi from '../api/Documents'
import { ProductApi } from '../api/Product'
import ProductModal from '../components/Main/Product/ProductModal'
import FormSectionTitle from '../elements/FormSectionTitle'
import withKeyValue from '../elements/HOC/WithKeyValue'
import withSort from '../elements/HOC/withSort'
import useFetchList from '../elements/Hooks/useFetchList'
import useFetchObject from '../elements/Hooks/useFetchObject'
import useFetchSelectUrl from '../elements/Hooks/useFetchSelectUrl'
import useModalData, { MODAL_TYPE_EDIT } from '../elements/Hooks/useModalData'
import useSubmit from '../elements/Hooks/useSubmit'
import CustomSelect from '../elements/Select/CustomSelect'
import withSelectFetch from '../elements/Select/withSelectFetch'
import { get, isEmpty, pick } from 'lodash'
import { boolLabelItemLayout, defaultFormItemLayout, fullWidthFormItemLayout, rules } from '../modules/FormItems'
import { strVal } from '../modules/Utils'
import React, { Fragment } from 'react'
import { compose } from 'recompose'
import { array } from "prop-types";

const docsApi = new DocumentsApi()

function ProductSelection(props) {
  const {
    getFieldDecorator,
    getFieldValue,
    getFieldsValue,
    doc,
    onChangeProductCategory,
    onChangeProductGroup,
    company: DocCompany,
    setCategory,
  } = props

  const company = DocCompany || getFieldValue('company._id') || get(doc, 'company._id')
  const isProduct = getFieldValue('isProduct')
  const productType = getFieldValue('productType') || get(doc, 'productType')
  const categoriesURL = `products/categories/dropdown?type=${productType}&owner=${company}&select=*`

  const [CategorySelect] = useFetchSelectUrl(
    {
      name: 'products_categories',
      url: categoriesURL,
      cache: true,
      forceFetch: true,
    },
    [categoriesURL, productType],
    false,
    undefined,
    (c) => c
  )

  const category = getFieldValue('category._id') || get(doc, 'category._id')
  const group = getFieldValue('group._id') || get(doc, 'group._id')
  const productID = getFieldValue('product._id') || ''
  const productKey = productID.replace('products/', '')
  const { object: product } = useFetchObject(
    () => ProductApi.read(productKey),
    undefined,
    [productKey],
    isEmpty(productID)
  )

  const [ProductsSelect] = useFetchSelectUrl(
    {
      name: 'products',
      url: `products/?owner=${company}&state=Active&category=${category}&group=${group}&select=*`,
      cache: false,
      forceFetch: true,
    },
    [company, category, group],
    false,
    Select
  )

  const [ProductsLinesSelect] = useFetchSelectUrl(
    {
      name: 'products_lines',
      url: `products/lines?product=${productID}&select=*`,
      forceFetch: true,
    },
    [productID],
    isEmpty(productID),
    Select,
    ({ name, ...product }) => ({
      name: `${name} - ${strVal(product, 'description')}`,
      ...product,
    })
  )
  const [GroupsSelect] = useFetchSelectUrl(
    {
      name: 'products_groups',
      url: `products/groups/dropdown?owner=${company}&category=${category}&select=*`,
      cache: true,
      forceFetch: true,
    },
    [company, category]
  )

  const {
    visible,
    modalType,
    object: productDB,
    submitting,
    submit,
    removing: removingProduct,
    remove,
    formRef,
    onShow,
    onCancel,
    update,
    setObject: setProduct,
  } = useModalData(ProductApi.create, ProductApi.update, ProductApi.remove, () => [])
  const isNew = modalType === 'new'

  const productsActions = useFetchList(
    () => docsApi.actions('products', product.status),
    [product.status],
    isEmpty(product.status)
  )
  const [submittingOperation, submitOperation] = useSubmit(ProductApi.operation)

  async function handleActions(operation) {
    let hide = message.loading('', 0)
    const productDB = await submitOperation(operation, pick(product, ['_key', '_rev', '_id']))
    if (operation === 'duplicate' && productDB) onCancel(true)

    if (productDB) setProduct(productDB)

    hide()
  }

  return (
    <Fragment>
      <ProductModal
        {...{
          visible,
          modalType,
          product: productDB || {
            company: { _id: company },
            type: 'Product',
            ...getFieldsValue(['category._id', 'group._id', 'product._id']),
            ...productDB,
          },
          submitting,
          submit,
          removingProduct,
          remove,
          formRef,
          onShow,
          onCancel,
          update,
          setProduct,
          isNew,
          isUpdatable: true,
          actions: productsActions.list,
          handleActions,
        }}
      />

      <FormSectionTitle title={'PRODUCT SELECTION'} />

      <Row>
        <Col span={8}>
          <Form.Item {...boolLabelItemLayout} label={'is a product'}>
            {getFieldDecorator('isProduct', {
              //initialValue: get(doc, 'isProduct') || true,
              initialValue: true,
              valuePropName: 'checked',
            })(<Checkbox disabled />)}
          </Form.Item>
        </Col>
      </Row>

      {isProduct && (
        <Row>
          <Col span={12}>
            <Form.Item {...defaultFormItemLayout} label={'Type'}>
              {getFieldDecorator('productType', {
                initialValue: get(doc, 'productType'),
                rules: rules.select,
              })(<ProductsTypesSelect mode="multiple"/>)}
            </Form.Item>
          </Col>

          {getFieldValue('productType') && (
            <Col span={12}>
              <Form.Item {...defaultFormItemLayout} label={'Category'}>
                {getFieldDecorator('category._id', {
                  initialValue: get(doc, 'category._id'),
                  rules: rules.select,
                })(
                  <CategorySelect
                    keyAtr={'_id'}
                    valueAtr={'name'}
                    setSelected={setCategory}
                    placeholder={'Category'}
                    onChange={onChangeProductCategory}
                  />
                )}
              </Form.Item>
            </Col>
          )}

          <Col span={12}>
            {getFieldValue('category._id') && (
              <Form.Item {...defaultFormItemLayout} label={'Group'}>
                {getFieldDecorator('group._id', {
                  initialValue: get(doc, 'group._id'),
                  rules: rules.select,
                })(
                  <GroupsSelect
                    placeholder={'Group'}
                    keyAtr={'_id'}
                    valueAtr={'name'}
                    onChange={onChangeProductGroup}
                  />
                )}
              </Form.Item>
            )}
          </Col>

          <Col span={12}>
            {getFieldValue('group._id') && (
              <Form.Item {...defaultFormItemLayout} label={'Product'}>
                {getFieldDecorator('product._id', {
                  initialValue: get(doc, 'product._id'),
                  rules: rules.select,
                })(
                  <ProductsSelect
                    style={{ width: '80%' }}
                    placeholder={'Products Type'}
                    keyAtr={'_id'}
                    valueAtr={'name'}
                  />
                )}
                <Icon style={{ marginLeft: 5 }} type="plus-circle" onClick={onShow} />
              </Form.Item>
            )}
          </Col>
          <Col span={24}>
            <Form.Item {...fullWidthFormItemLayout} label={'SKU'}>
              {getFieldDecorator('productLine._id', {
                initialValue: get(doc, 'productLine._id'),
                rules: rules.select,
              })(
                <ProductsLinesSelect
                  style={{ width: '80%' }}
                  placeholder={'Product'}
                  keyAtr={'_id'}
                  valueAtr={'name'}
                  disable={!getFieldValue('product._id')}
                />
              )}
              <Icon style={{ marginLeft: 5 }} type="plus-circle" onClick={() => onShow(product, MODAL_TYPE_EDIT)} />
            </Form.Item>
          </Col>
        </Row>
      )}
    </Fragment>
  )
}

ProductSelection.propTypes = {}
ProductSelection.defaultProps = {
  onChangeProductCategory: () => console.log(''),
  onChangeProductGroup: () => console.log(''),
}

const ProductsTypesSelect = compose(
  withSelectFetch({ dropdown: 'products', name: 'type' }),
  withKeyValue(),
  withSort
)(CustomSelect)

export default ProductSelection
